import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './Rules.css';
import useStore from '../store/store';

const Rules = () => {
  const [activeView, setActiveView] = useState('auction');
  const location = useLocation();
  const storeSeason = useStore((s) => s.currentSeason);
  const showPrizePool = storeSeason?.show_prize_pool === true;
  const prizePoolDisplay = storeSeason?.prize_pool_display || '₹1,00,000';
  const prizePoolFirst = storeSeason?.prize_pool_first || '₹65,000';
  const prizePoolSecond = storeSeason?.prize_pool_second || '₹35,000';

  useEffect(() => {
    // Read query param ?tab=prizepool or #prizepool to set active tab
    try {
      const params = new URLSearchParams(location.search);
      const tab = params.get('tab');
      if (tab) {
        const allowed = ['auction', 'tournament', 'prizepool'];
        if (allowed.includes(tab)) {
          // If prizepool is hidden, redirect to auction tab silently
          if (tab === 'prizepool' && !showPrizePool) {
            setActiveView('auction');
          } else {
            setActiveView(tab);
          }
        }
      }
    } catch (e) {
      // ignore
    }
  }, [location.search, showPrizePool]);

  // Only show prizepool tab if revealed in Supabase
  const views = [
    { id: 'auction', name: 'Auction' },
    { id: 'tournament', name: 'Tournament' },
    ...(showPrizePool ? [{ id: 'prizepool', name: 'Prize Pool' }] : []),
  ];

  const renderAuctionRules = () => (
    <div className="rules-content">
      <div className="rule-section">
        <h2>1. Player Base Prices</h2>
        <p>Player base prices are fixed according to their rank:</p>
        <div className="price-table">
          <div className="price-row">
            <span className="rank platinum">Platinum:</span>
            <span className="points">4 points</span>
          </div>
          <div className="price-row">
            <span className="rank diamond">Diamond:</span>
            <span className="points">8 points</span>
          </div>
          <div className="price-row">
            <span className="rank ascendant">Ascendant:</span>
            <span className="points">12 points</span>
          </div>
          <div className="price-row">
            <span className="rank immortal">Immortal:</span>
            <span className="points">16 points</span>
          </div>
          <div className="price-row">
            <span className="rank radiant">Radiant:</span>
            <span className="points">25 points</span>
          </div>
        </div>
        <p>
          Team owners who also participate as players will have their value directly deducted from the team's budget.
        </p>
      </div>

      <div className="rule-section">
        <h2>2. Team Budget and Squad Size</h2>
        <ul>
          <li>
            Each team starts with <strong>150 points</strong>.
          </li>
          <li>
            Teams must complete a roster of <strong>6 players</strong> (including the owner if playing).
          </li>
          <li>The owner's deducted points reduce the total available balance for the team. See <strong>Section 8</strong> for full deduction details and examples.</li>
        </ul>
      </div>

      <div className="rule-section">
        <h2>3. Roster Completion and Penalty</h2>
        <ul>
          <li>Teams must fill all 6 roster slots before the auction ends.</li>
          <li>If a team overspends and cannot complete their roster:</li>
          <ul>
            <li>The admin will assign a player based on the points remaining.</li>
            <li>Example: If only 7 points are left, the team will be assigned a Platinum player.</li>
            <li>The assignment follows a descending order of rank.</li>
          </ul>
          <li>
            Any player assigned by tournament administration due to roster completion requirements must play at least <strong>1 official group-stage match</strong> during the season.
          </li>
          <li>
  Failure to field the assigned player without tournament administration approval may result in penalties, including default match losses.
</li>
        </ul>
      </div>

<div className="rule-section">
  <h2>4. Player Rank, Eligibility & Verification</h2>

  <ul>
    
   <li>
  Player auction values will be determined based on their rank history across the most recent 5 competitive acts preceding the auction.
</li>

<li>
  Tournament administration will review a player's highest achieved rank, rank consistency, and competitive history during those 5 acts when determining their auction value.
</li>

<li>
  This system is designed to provide fair player valuations while preventing rank manipulation and maintaining competitive balance.
</li>

    <li>
      Players must provide accurate tracker, rank, role, and registration information.
    </li>

    <li>
      Any player found providing false rank information, using alternate accounts, smurf accounts, or misleading tracker information — whether discovered before or after the auction — will be <strong>immediately and permanently banned from VPL</strong>. The team that purchased the player will receive a full refund of the points spent on that player and will be assigned a replacement from the unsold player pool at no additional points cost. If no suitable replacement is available in the unsold pool, tournament administration will determine an appropriate resolution at their discretion.
    </li>

    <li>
      Players registering for the auction are expected to be available throughout the season.
    </li>

    <li>
      Players must disclose any major availability limitations before the auction.
    </li>

    <li>
      Players sold during the auction are expected to honor their commitment to the team that purchases them.
    </li>

    <li>
      Refusal to play for a team after being successfully purchased may result in tournament penalties.
    </li>
    <li>
  Players are responsible for providing accurate availability information during registration.
</li>

<li>
  Team owners are expected to review a player's declared availability before placing bids.
</li>

<li>
  Player availability conflicts declared prior to the auction cannot be used as grounds for roster disputes, refunds, auction reversals, or compensation requests.
</li>
  </ul>

  <div className="example-box">
    <p>
      <strong>Note:</strong> Tournament administration may review a player's rank progression across the most recent 5 competitive acts, tracker information, 
      and competitive record to determine an appropriate starting auction value.
    </p>
  </div>
</div>
      <div className="rule-section">
        <h2>5. Unsold Players</h2>
        <ul>
          <li>After the main auction, all unsold players will be auctioned again in a final unsold round.</li>
          <li>
            To prevent last-moment sniping, each unsold player will have <strong>+2 points</strong> added to their base
            price.
          </li>
        </ul>

        <div className="example-box">
          <p>
            <strong>Example:</strong> Platinum increases from 4 → 6 points, Diamond from 8 → 10 points.
          </p>
        </div>

        <p>
          Players who remain unsold even after this round will still be available as replacements during the season, in
          case teams require changes.
        </p>
      </div>

      <div className="rule-section">
        <h2>6. Auction Decisions</h2>
        <ul>
          <li>
            The auctioneer's decision is <strong>final</strong>.
          </li>
          <li>Once a player is declared as "SOLD" by the auctioneer, there will be no changes or reversals.</li>
          <li>Slow or fast sales declared by the auctioneer cannot be contested.</li>
          <li>
  Tournament administration reserves the right to correct clerical, technical, or recording errors discovered during or immediately after the auction.
</li>
        </ul>
      </div>

      <div className="rule-section">
  <h2>7. Owner Participation &amp; Bidding Authority</h2>
  <ul>
    <li>
      Every team must register one Primary Owner before the auction begins.
    </li>

    <li>
      There are two types of owners — <strong>Playing Owner</strong> and <strong>Non-Playing Owner</strong>. Owners must declare their type before auction registration closes. This declaration cannot be changed once the auction begins.
    </li>

    <li>
      <strong>Playing Owner:</strong> An owner who registers as a participating player in the tournament. Playing owners must register through the standard player registration process and will have the predefined owner deduction applied to their team's starting budget. The playing owner is responsible for placing bids during the auction.
    </li>

    <li>
      <strong>Non-Playing Owner:</strong> An owner who does not participate as a player in the tournament. A non-playing owner may either:
      <ul>
        <li>Attend the auction and place bids themselves, or</li>
        <li>Assign a representative to place bids and manage the team during the auction on their behalf.</li>
      </ul>
    </li>

    <li>
      <strong>If a non-playing owner assigns a representative, that representative must not be a player registered in the auction pool.</strong> Assigning a registered auction player as a representative — regardless of reason — is strictly prohibited and will result in immediate disqualification of the team.
    </li>

    <li>
      Only the registered Primary Owner or their approved representative may place bids during the auction. No other person may bid on behalf of the team under any circumstances.
    </li>

    <li>
      If a Primary Owner is unable to attend the auction and has not assigned a representative in advance, tournament administration must be informed before the auction begins. Tournament administration may approve an emergency representative at their discretion.
    </li>

    <li>
      Ownership cannot be transferred after auction registration closes.
    </li>

    <li>
      Any attempt to exploit owner registration — including registering a strong player as a non-playing owner's representative to avoid auction pricing, false participation declarations, or substitute ownership arrangements — will result in immediate disqualification, auction reversal, and a permanent ban from future seasons.
    </li>
  </ul>
</div>
<div className="rule-section">
  <h2>8. Owner Deductions</h2>

  <ul>
    <li>
      Playing owners will have a predefined deduction applied before the auction begins.
    </li>

    <li>
      Owner deductions will be determined by tournament administration.
    </li>

    <li>
      Factors considered may include rank history, previous VPL performance, competitive experience, and overall impact on competitive balance.
    </li>

    <li>
      Owner deduction values will be announced before the auction begins.
    </li>

    <li>
      Once announced, owner deduction values are final and cannot be challenged or modified.
    </li>
  </ul>

  <div className="example-box">
    <p>
      <strong>Example:</strong> If an owner's deduction is determined as 22 points,
      the team will begin the auction with 128 points instead of 150.
    </p>
  </div>
</div>

      <div className="rule-section">
        <h2>9. Backup Responsibility</h2>
        <ul>
          <li>
            All owners must have a <strong>backup plan</strong> prepared in case of power outages, internet failures, or
            technical issues.
          </li>
          <li>This may include having a mobile hotspot, inverter backup, or an approved emergency representative ready if required.</li>
          <li>
            It is the owner's responsibility to ensure continuous participation during the entire auction process.
          </li>
          <li>
  Failure to attend the auction without prior notice may result in administrative action at the tournament administration's discretion.
</li>
        </ul>
      </div>
    </div>
    
  );

  const renderPrizePool = () => (
    <div className="rules-content">
      <h1>Season 5 Prize Pool</h1>

      <div className="pp2">
        {/* Total prize band */}
        <div className="pp2-total">
          <div className="pp2-total-text">
            <span className="pp2-total-label">Total Prize Pool</span>
            <span className="pp2-total-amount">{prizePoolDisplay}</span>
            <span className="pp2-total-sub">Season 5 Championship</span>
          </div>
          <div className="pp2-total-icon">
            <svg viewBox="0 0 24 24" width="64" height="64" fill="none" stroke="#f4c84a" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
              <path d="M7 5H4v1.5A3.5 3.5 0 0 0 7.5 10" />
              <path d="M17 5h3v1.5A3.5 3.5 0 0 1 16.5 10" />
              <path d="M9.5 14.5 9 18h6l-.5-3.5" />
              <path d="M8 21h8" />
            </svg>
          </div>
        </div>

        {/* Tiers */}
        <div className="pp2-tiers">
              <div className="pp2-tier pp2-champion">
            <div className="pp2-tier-inner">
              <span className="pp2-glow" />
              <div className="pp2-tier-head">
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#f4c84a" strokeWidth="1.5" strokeLinejoin="round">
                  <path d="M3 8.5 6.5 11 12 5l5.5 6L21 8.5 19.4 18.5H4.6L3 8.5Z" />
                  <path d="M4.7 18.5h14.6" />
                </svg>
                <span>Champions</span>
              </div>
              <div className="pp2-tier-amount">{prizePoolFirst}</div>
              <div className="pp2-villa">+ Luxurious Villa Stay for One Day</div>
            </div>
          </div>

          <div className="pp2-tier pp2-runnerup">
            <div className="pp2-tier-inner">
              <div className="pp2-tier-head">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#c8d0d8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8.5 3 11 8.5" />
                  <path d="M15.5 3 13 8.5" />
                  <circle cx="12" cy="15" r="6" />
                  <path d="m12 12.4 1 2 2.1.3-1.5 1.5.3 2.1-1.9-1-1.9 1 .3-2.1L9 14.7l2.1-.3 .9-2Z" />
                </svg>
                <span>Runners Up</span>
              </div>
              <div className="pp2-tier-amount">{prizePoolSecond}</div>
              <div className="pp2-tier-note">Second place finishers</div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="pp2-notes">
          <p>
            <span className="pp2-ast">∗</span> Please note, the prize amounts shown are provisional; a 30% tax will be
            deducted at source from the gross winnings, so the final payout will be the net after tax
          </p>
          <p>
            <span className="pp2-ast">∗</span> Villa stay details will be provided to the winning team
          </p>
          <p>
            <span className="pp2-ast">∗</span> Prize distribution within teams will be managed by team owners
          </p>
        </div>
      </div>
    </div>
  );

  // eslint-disable-next-line no-unused-vars
  const renderTournamentRules = () => (
    <div className="rules-content">
      <div className="coming-soon-section">
        <div className="coming-soon-icon">🏆</div>
        <h2>Tournament Rules</h2>
        <p>Season 5 tournament rules are being finalized and will be published here soon.</p>
        <div className="coming-soon-details">
          <p>Check back closer to the league stage, or follow our Discord for updates.</p>
        </div>
      </div>
    </div>
  );

  const renderTournamentRulesFull = () => (
    <div className="rules-content">
      <h1>Valorant Premier Legends Season 5 – Official Tournament Rules</h1>

      <div className="rule-section">
        <h2>Tournament Format</h2>
        <ul>
          <li className="important-rule">
            <span className="highlight-text">GROUP STAGE:</span> 16 teams will be split into
            <span className="rule-emphasis"> 2 groups of 8 teams</span> each. Every team plays a full round-robin
            within their group — <span className="rule-emphasis">6 matches per team</span> in the group stage.
          </li>
          <li className="important-rule">
            <span className="highlight-text">PLAYOFFS QUALIFICATION:</span>
            <ul>
              <li>
                <span className="rule-emphasis">Top 2 teams</span> from each group qualify directly for the
                <span className="rule-emphasis"> Upper Bracket</span>.
              </li>
              <li>
                <span className="rule-emphasis">3rd and 4th place teams</span> from each group qualify for the
                <span className="rule-emphasis"> Lower Bracket</span>.
              </li>
              <li>Teams finishing 5th or lower in their group are eliminated.</li>
            </ul>
          </li>
          <li className="important-rule">
            <span className="highlight-text">MATCH FORMAT:</span>
            <ul>
              <li>
                <span className="rule-emphasis">Group Stage:</span> All group stage matches are
                <span className="rule-emphasis"> Best of 1 (BO1)</span>.
              </li>
              <li>
                <span className="rule-emphasis">Playoffs:</span> Upper Bracket, Lower Bracket, and Lower Bracket Final
                are all played as <span className="rule-emphasis">Best of 3 (BO3)</span>.
              </li>
              <li>
                <span className="rule-emphasis">Grand Final:</span> Best of 3 (BO3).
              </li>
            </ul>
          </li>
          <li className="important-rule">
            <span className="highlight-text">TIEBREAKERS:</span> If two or more teams are tied on points at the end of
            the group stage, standings are decided in this order:
            <ul>
              <li><span className="rule-emphasis">Points</span> (3 per win, 0 per loss)</li>
              <li><span className="rule-emphasis">Round Differential</span> (rounds won − rounds lost)</li>
              <li><span className="rule-emphasis">Total Rounds Won</span></li>
            </ul>
            <span className="rule-emphasis"> Overtime note:</span> If a group stage match goes into overtime (both
            teams reach 12), that match's rounds are not counted towards round differential or total rounds won —
            only the win/loss is recorded.
          </li>
          <li className="important-rule">
            <span className="highlight-text">PLAYOFF SEEDING:</span> Cross-group seeding to keep top teams from
            meeting early:
            <ul>
              <li>Group A #1 plays Group B #2 (Upper Bracket)</li>
              <li>Group A #2 plays Group B #1 (Upper Bracket)</li>
              <li>Group A #3 plays Group B #4 (Lower Bracket)</li>
              <li>Group A #4 plays Group B #3 (Lower Bracket)</li>
            </ul>
          </li>
          <li>
            The complete bracket, seedings, and match schedule will be published on the official website before the
            playoffs begin.
          </li>
        </ul>
      </div>

      <div className="rule-section">
        <h2>Core Tournament Guidelines</h2>
        <ul>
          <li className="important-rule">
            <span className="highlight-text">IMPORTANT:</span> All players must use their registered main Riot account
            for the tournament. Using alternate accounts or smurfs is strictly prohibited.
          </li>
          <li>By participating, players agree to follow all tournament rules and admin decisions.</li>
          <li className="warning-rule">
            <span className="penalty-text">ZERO TOLERANCE:</span> Players must show respect towards admins and other
            participants. Toxic or disrespectful behavior will lead to immediate penalties. This applies to all
            official VPL channels including Discord, in-game chat, streams, and any tournament-related communication.
          </li>
          <li>
            Team captains must join the official Discord server for communication with admins. The link will be shared
            before the event starts.
          </li>
          <li className="important-rule">
            <span className="highlight-text">MATCH INTEGRITY:</span> Players must always play to win. Throwing or
            intentionally losing matches is strictly prohibited and will result in disqualification.
          </li>
          <li className="warning-rule">
            Any offensive, vulgar, racist, or inappropriate names and chat messages will result in immediate action.
          </li>
          <li>
            All participants must be <span className="rule-emphasis">16 years or older</span> to compete in the
            tournament.
          </li>
          <li className="important-rule">
            <span className="highlight-text">PRIZE POOL DISTRIBUTION:</span> By default, the entire prize pool is
            distributed among the team members based on the team's internal agreement. Team owners are
            <span className="rule-emphasis"> not required</span> to take any share of the prize pool.
          </li>
          <li>
            However, owners may choose to allocate up to
            <span className="rule-emphasis"> 25% of the prize pool</span> to themselves as a token of appreciation for
            their investment and effort in building the team and supporting the league. This is
            <span className="rule-emphasis"> completely optional</span> and is decided by the team owner, not enforced
            by the tournament.
          </li>
          <li>
            The rest of the prize pool (or the full amount, if the owner takes no share) is split among the team
            members. This split should be agreed upon
            <span className="rule-emphasis"> before the tournament begins</span>. Tournament admins will step in to
            resolve disputes regarding the player split — not the owner's optional share.
          </li>
        </ul>
      </div>

      <div className="rule-section">
        <h2>Scheduling Rules</h2>
        <ul>
          <li>
            The tournament will run for approximately <span className="rule-emphasis">4–5 weeks</span>, depending on
            match progress.
          </li>
          <li className="important-rule">
            A total of <span className="highlight-text">16 teams</span> will participate. The schedule is strict and
            must be followed by all teams.
          </li>
          <li>
            Each team is allowed a maximum of <span className="rule-emphasis">two reschedules</span> during the
            tournament.
          </li>
          <li className="warning-rule">
            <span className="penalty-text">CRITICAL:</span> Any reschedule request must be made at least 24 hours prior
            to the match by informing the admins. Late requests will not be considered.
          </li>
          <li className="important-rule">
            <span className="highlight-text">MATCH REQUIREMENTS:</span> Teams must have 5 players present at the
            scheduled time. Matches cannot be played with only 4 players and will result in a walkover.
          </li>
          <li className="important-rule">
            <span className="highlight-text">WALKOVER:</span> A walkover awards the opposing team the
            <span className="rule-emphasis"> win (3 points)</span> in the standings, along with a
            <span className="rule-emphasis"> minimal 1-0 map score</span> to register the result. Walkovers do
            <span className="rule-emphasis"> NOT</span> contribute meaningful round differential or rounds won
            towards tiebreakers.
          </li>
          <li>
            <span className="highlight-text">WHY THIS CHANGED FROM LAST SEASON:</span> In Season 4, walkovers were
            awarded as full <span className="rule-emphasis">13-0 wins</span>, which meant a single no-show could
            drastically inflate one team's round differential and unfairly affect other teams' qualification
            chances. After reviewing the impact, we're moving to a
            <span className="rule-emphasis"> more balanced approach</span> — the winning team still gets their 3
            points, but the round-based tiebreaker credit only goes to teams that actually play. This keeps the
            standings fair for everyone in the group, especially teams battling for playoff spots.
          </li>
          <li>
            Walkovers are only applied when a team is unwilling or unable to field 5 players within the grace
            period below.
          </li>
          <li className="important-rule">
            <span className="highlight-text">LATE ARRIVAL:</span> If a team is not fully checked in with 5 players by
            <span className="rule-emphasis"> 15 minutes past the scheduled start time</span>, the situation is
            handled as follows:
            <ul>
              <li>
                If the opposing team is willing to wait, admins may grant additional time. There is no penalty for
                waiting.
              </li>
              <li>
                If the opposing team is not willing to wait beyond the 15-minute grace period, admins will award a
                walkover to the opposing team.
              </li>
              <li>
                Walkovers will only be enforced in cases of clear indiscipline or repeated no-shows. Admins will use
                discretion for genuine, one-off issues.
              </li>
            </ul>
          </li>
          <li className="warning-rule">
            <span className="penalty-text">PUNCTUALITY:</span> If a team arrives late (past the 15-minute grace
            period) <span className="rule-emphasis">two or more times</span> in the tournament, they will
            automatically lose one reschedule opportunity.
          </li>
          <li>The schedule will be updated weekly and published on the official website.</li>
          <li>Team owners are responsible for checking the schedule regularly for updates.</li>
          <li className="important-rule">
            <span className="highlight-text">CHECK-IN POLICY:</span> Teams must check in 15–20 minutes before match time
            with cameras turned on. Late check-ins may result in penalties.
          </li>
          <li>
            <span className="rule-emphasis">Note:</span> Check-in and late arrival are tracked on different clocks —
            check-in happens
            <span className="rule-emphasis"> before</span> the scheduled match start (15–20 min prior), while the
            15-minute late-arrival grace period only begins
            <span className="rule-emphasis"> after</span> the scheduled start time. A team can be penalized for late
            check-in, late arrival, or both depending on the situation.
          </li>
          <li>All participants must commit to playing throughout the entire tournament duration.</li>
          <li className="warning-rule">
            Repeated schedule violations or communication failures will result in disqualification.
          </li>
        </ul>
      </div>

      <div className="rule-section">
        <h2>Agent Selection & Re-Lobby Policy</h2>
        <ul>
          <li className="important-rule">
            <span className="highlight-text">GENUINE PICKS:</span> Players must select the agents they actually intend
            to play during the Agent Selection phase. Locking a random or joke agent just to bait the enemy team's
            composition — with the intention of switching later — is strictly prohibited.
          </li>
          <li className="warning-rule">
            <span className="penalty-text">RE-LOBBY AGENT LOCK:</span> If a match is re-lobbied for any reason
            (technical issue, one team accidentally revealing picks, admin decision, etc.),
            <span className="rule-emphasis"> all players on both teams must select the exact same agents they had
              picked in the original lobby</span>. Changing agents after a re-lobby will be treated as exploiting the
            re-lobby to counter-pick and is a rule violation.
          </li>
          <li>
            Agent changes after a re-lobby are only permitted with
            <span className="rule-emphasis"> explicit approval from a tournament admin</span>, and only for
            legitimate reasons (e.g., the re-lobby happened before any picks were locked, or a genuine mistake in the
            original lobby).
          </li>
          <li className="important-rule">
            <span className="highlight-text">STREAM EVIDENCE:</span> Agent Selection is recorded on the official
            tournament stream. Recorded picks will be used as evidence in the event of a dispute or re-lobby.
          </li>
          <li>
            Players and teams are responsible for confirming their agent selection carefully before locking in.
            Careless or troll picks that trigger a re-lobby may result in the offending team forfeiting the map or match.
          </li>
        </ul>
      </div>

      <div className="rule-section">
        <h2>Match Integrity & Technical Guidelines</h2>
        <ul>
          <li className="important-rule">
            <span className="highlight-text">TECHNICAL TIMEOUT:</span> Each team is allowed one timeout of up to
            <span className="rule-emphasis"> 15 minutes per side</span> during the match for power outages or internet
            issues.
          </li>
          <li className="important-rule">
            <span className="highlight-text">BACKUP PLAYER:</span> It is highly recommended to have a sixth (backup)
            player ready at all times.
          </li>
          <li>
            The backup player may log in and play only if the original player cannot return after the 15-minute
            technical timeout — or before the match starts, if the original player is unavailable at start time.
            <span className="rule-emphasis"> Once the backup subs in, the original player cannot log back in for that
            match.</span>
          </li>
          <li>
            Teams may use their backup player in every match if needed — the sub is not limited to one usage across
            the tournament.
          </li>
          <li className="important-rule">
            <span className="highlight-text">SERVER:</span> All matches will be played on the
            <span className="rule-emphasis"> Mumbai server</span>. Server-related disputes citing other regions will
            not be accepted.
          </li>
          <li className="important-rule">
            <span className="highlight-text">STREAM DELAY:</span> Any player streaming their own POV during a live
            match must maintain a minimum
            <span className="rule-emphasis"> 3-minute delay</span> on their stream to prevent stream sniping. This
            applies to all platforms (Twitch, YouTube, Kick, etc.).
          </li>
          <li className="warning-rule">
            <span className="penalty-text">ADMIN AUTHORITY:</span> Tournament admins have full authority to pause,
            resume, or reset matches in case of technical issues, server problems, or suspicious activity.
          </li>
          <li className="important-rule">
            Teams must cooperate with admins to resolve technical problems promptly.
            <span className="highlight-text"> Uncooperative behavior may result in penalties.</span>
          </li>
        </ul>
      </div>

      <div className="rule-section">
        <h2>Rule Violation and Punishment</h2>
        <ul>
          <li>
            Any complaint against a player from another team must be submitted by the complainant to
            <span className="rule-emphasis"> their own team owner</span>, who then raises it with the tournament
            admins. This keeps player-to-player confrontations out of the process and puts the responsibility on
            owners to handle disputes cleanly.
          </li>
          <li>
            The protest must include a clear and valid reason, details about when and why the violation occurred, the
            name of the player involved, and a description of the incident.
          </li>
          <li className="important-rule">
            <span className="highlight-text">DIRECT ADMIN ACTION:</span> Tournament admins may act on rule violations
            they directly witness (in-VC toxicity, stream chat, in-game text/voice, Discord) without waiting for a
            formal complaint from an owner. This is separate from the standard complaint process and applies to
            behavior admins observe in real time.
          </li>
          <li>
            Punishments for rule violations may include but are not limited to:
            <ul>
              <li>Loss of one match</li>
              <li>Disqualification from the tournament</li>
              <li>Ban from participating in future tournaments</li>
            </ul>
          </li>
        </ul>
      </div>

      <div className="rule-section">
        <h2>Penalties for Intentional Delays and False Technical Claims</h2>
        <ul>
          <li>
            Any team or player found intentionally delaying the match without valid reason will face penalties, such as:
            <ul>
              <li>Immediate forfeiture of the match</li>
              <li>Deduction of one reschedule chance for future matches</li>
              <li>Disqualification from the tournament for repeated offenses</li>
            </ul>
          </li>
          <li>
            False claims of technical issues will be investigated by the admin team. If proven false or abused:
            <ul>
              <li>
                The team may be penalized with loss of the match, deduction of future technical timeouts, or
                disqualification from the event
              </li>
              <li>Responsible players may be banned for up to two seasons if the abuse is repeated or malicious</li>
            </ul>
          </li>
          <li>Admins reserve the right to review evidence and make final decisions on any disputed technical claim</li>
        </ul>
      </div>

      <div className="rule-section">
        <h2>Replacement Policy</h2>
        <ul>
          <li>
            Replacements are allowed only for genuine emergencies, verified and approved by the tournament admins.
          </li>
          <li>If approved, no bans or penalties will be applied to the departing player.</li>
          <li>
            Players who try to withdraw for silly or non-serious reasons, or show disinterest after being picked, will
            receive a 2-season ban from future tournaments.
          </li>
          <li>
            Replacements will not be approved for internal team issues or poor performance. Team owners are responsible
            for resolving such matters within their team.
          </li>
          <li>
            In the case of an emergency replacement:
            <ul>
              <li>The admin team will first look for an available unsold player to fill the slot.</li>
              <li>
                If no unsold players are available, the admin team may assign a replacement player from their side.
              </li>
              <li>
                If still unresolved, a voting system among all team owners will decide whether the proposed replacement
                can join, based on majority approval.
              </li>
              <li>
                If the admin team cannot assign a suitable replacement, the team owner may suggest one, subject to the
                same voting process among owners.
              </li>
            </ul>
          </li>
        </ul>
      </div>

      <div className="rule-section">
        <h2>Other Important Rules</h2>
        <ul>
          <li>
            Cheating and Third-Party Software: Any use of cheats, hacks, or unauthorized software will result in
            immediate disqualification and a ban from future tournaments.
          </li>
          <li>
            Account Sharing and Dual Team Membership: Players cannot play on multiple teams or allow others to use their
            Riot account. Sharing accounts or dual membership leads to disqualification.
          </li>
          <li>
            Spectator and Streaming Policy: Only authorized streamers and tournament officials may spectate or broadcast
            matches. Unauthorized streaming or spectator access is not permitted.
          </li>
          <li className="important-rule">
            <span className="highlight-text">PRIZE POOL RELEASE:</span> The prize pool will be distributed after the
            Grand Final concludes. Teams will be paid out based on the confirmed team split submitted before the
            tournament began.
          </li>
          <li className="warning-rule">
            <span className="penalty-text">RESPECT ADMIN DECISIONS:</span> Once tournament admins make a decision on
            any dispute, rule violation, or match-related incident, all players and owners must accept it publicly.
            Publicly criticizing, disputing, or attacking admin decisions on social media, streams, or public
            channels after a ruling has been made will result in penalties, including possible suspension or
            disqualification. Concerns should be raised privately through the proper channels.
          </li>
          <li>Tournament admins have the right to update or change rules anytime to maintain fair play.</li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="rules-container">
      <div className="rules-header">
        <h1>VPL Rules</h1>
        <div className="rules-underline"></div>
      </div>

      {/* Tab Navigation */}
      <div className="rules-tabs">
        {views.map((view) => (
          <div
            key={view.id}
            className={`rules-tab ${activeView === view.id ? 'active' : ''}`}
            onClick={() => setActiveView(view.id)}
          >
            <span className="tab-name">{view.name}</span>
          </div>
        ))}
      </div>

      {/* Conditional Content Rendering */}
      {activeView === 'auction' && renderAuctionRules()}
      {activeView === 'tournament' && renderTournamentRulesFull()}
      {activeView === 'prizepool' && renderPrizePool()}
    </div>
  );
};

export default Rules;